import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { Bookmark, Check, X, RotateCcw, Clock, ChevronDown, ChevronRight } from 'lucide-react';

const STATUS_COLORS = {
  pendente:   'bg-yellow-100 text-yellow-800',
  confirmada: 'bg-green-100 text-green-800',
  cancelada:  'bg-red-100 text-red-800',
  expirada:   'bg-gray-100 text-gray-600',
};

function fmtBRL(v) {
  if (v == null || v === '') return '—';
  return parseFloat(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function fmtData(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function venceu(r) {
  return r.status === 'pendente' && r.expira_em && new Date(r.expira_em) < new Date();
}

export default function PreReservasList() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('todas');
  const [aberta, setAberta] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pre-reservas'],
    queryFn: () => api.get('/pre-reservas').then(r => r.data.data),
    refetchInterval: 30000,
  });

  const mut = useMutation({
    mutationFn: ({ id, status }) => api.put(`/pre-reservas/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pre-reservas'] }),
  });

  const setStatus = (id, status) => {
    if (status === 'cancelada' && !window.confirm('Cancelar esta pré-reserva? A unidade volta a ficar disponível.')) return;
    mut.mutate({ id, status });
  };

  const rows = data || [];
  const filtered = filter === 'todas' ? rows : rows.filter(r => r.status === filter);

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <Bookmark size={20} className="text-primary" />
        <h1 className="text-xl font-bold text-gray-800">Pré-Reservas</h1>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Pré-reservas feitas pelo corretor no portal de vendas. A unidade fica bloqueada enquanto pendente.
        Vale por 6h ou até análise — passou das 6h aparece o aviso <Clock size={12} className="inline" />, mas nada muda sozinho.
        Confirmar mantém a unidade reservada; Cancelar libera a unidade.
      </p>

      <div className="flex gap-2 mb-4 flex-wrap">
        {['todas', 'pendente', 'confirmada', 'cancelada', 'expirada'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filter === s ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-center text-gray-400 py-12">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-400 py-12">Nenhuma pré-reserva encontrada.</div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="w-6 px-2 py-3" />
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Data</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Cliente</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Unidade</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Corretor</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Fluxo</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Status</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(r => (
                <React.Fragment key={r.id}>
                  <tr className="hover:bg-gray-50 transition cursor-pointer" onClick={() => setAberta(aberta === r.id ? null : r.id)}>
                    <td className="px-2 py-3 text-gray-400">{aberta === r.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{fmtData(r.data_criacao)}</td>
                    <td className="px-4 py-3"><div className="font-medium">{r.cliente_nome}</div><div className="text-xs text-gray-400">{r.cliente_cpf || ''}</div></td>
                    <td className="px-4 py-3"><span className="font-medium">{r.unidade_numero || '—'}</span><div className="text-xs text-gray-400">{r.empreendimento || ''}</div></td>
                    <td className="px-4 py-3 text-gray-600">{r.corretor_nome || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.fluxo_nome || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-800'}`}>{r.status}</span>
                      {venceu(r) && <div className="text-[11px] text-amber-700 mt-1 flex items-center gap-1"><Clock size={11} /> passou das 6h</div>}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        {r.status === 'pendente' && (
                          <>
                            <button onClick={() => setStatus(r.id, 'confirmada')} className="text-green-600 hover:text-green-800 text-xs font-medium flex items-center gap-1"><Check size={14} /> Confirmar</button>
                            <button onClick={() => setStatus(r.id, 'cancelada')} className="text-red-600 hover:text-red-800 text-xs font-medium flex items-center gap-1"><X size={14} /> Cancelar</button>
                          </>
                        )}
                        {r.status !== 'pendente' && (
                          <button onClick={() => setStatus(r.id, 'pendente')} className="text-gray-500 hover:text-gray-700 text-xs font-medium flex items-center gap-1"><RotateCcw size={14} /> Reabrir</button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {aberta === r.id && (
                    <tr className="bg-gray-50">
                      <td />
                      <td colSpan={7} className="px-4 py-4">
                        <div className="grid md:grid-cols-2 gap-6 text-sm">
                          <div>
                            <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Cliente</div>
                            <div>{r.cliente_nome} · {r.cliente_cpf || '—'}</div>
                            <div className="text-gray-500">{r.cliente_endereco || '—'}</div>
                            <div className="text-gray-500">{r.cliente_estado_civil || '—'} · {r.cliente_profissao || '—'}</div>
                            <div className="text-gray-500">{r.cliente_email} · {r.cliente_telefone}</div>
                            <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mt-3 mb-1">Corretor</div>
                            <div>{r.corretor_nome || '—'}{r.corretor_whatsapp ? ` · ${r.corretor_whatsapp}` : ''}{r.corretor_email ? ` · ${r.corretor_email}` : ''}</div>
                            {r.expira_em && <div className={`mt-3 text-xs flex items-center gap-1 ${venceu(r) ? 'text-amber-700' : 'text-gray-400'}`}><Clock size={12} /> Prazo: {fmtData(r.expira_em)}</div>}
                          </div>
                          <div>
                            <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">Condições de compra</div>
                            <div className="whitespace-pre-wrap text-gray-700">{r.condicoes_compra || '—'}</div>
                            {r.permuta && <>
                              <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mt-3 mb-1">Permuta</div>
                              <div className="whitespace-pre-wrap text-gray-700">{r.permuta}</div>
                            </>}
                            {r.valor_entrada && <div className="mt-3 text-gray-500">Entrada estimada: <span className="font-semibold">{fmtBRL(r.valor_entrada)}</span></div>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
